// src/pages/site/Home.js
// Public landing page at "/", for everyone (the app's dashboard is /dashboard). Google's OAuth verification reviews this page: it must name the app,
// explain what it does and why it asks for Google data, and link the Privacy Policy.
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout, { SITE } from '../../components/site/SiteLayout';
import { useStore } from '../../store';
import { getPlatform } from '../../utils/platforms';

const SUPPORTED = ['facebook', 'instagram', 'linkedin', 'x', 'youtube', 'gmb'];

const icon = (d) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
);

const FEATURES = [
  { title: 'Publish everywhere at once', text: 'Write a post once and send it to any mix of your connected pages, profiles and channels, now or at a time you choose.',
    icon: icon(<><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></>) },
  { title: 'Visual content calendar', text: 'See every scheduled and published post on one calendar, spot gaps in your plan and open any post to change it.',
    icon: icon(<><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>) },
  { title: 'Tailored for each network', text: 'Adjust the caption and media per platform, with character limits checked as you type, so every post fits where it lands.',
    icon: icon(<><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z"/></>) },
  { title: 'Account groups', text: 'Group the accounts that belong to a brand or client, then publish to the whole group in a single click.',
    icon: icon(<><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></>) },
  { title: 'Bulk scheduling', text: 'Import a whole month of posts from a CSV file instead of scheduling them one by one.',
    icon: icon(<><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M12 18v-6M9 15l3 3 3-3"/></>) },
  { title: 'Drafts and approvals', text: 'Save ideas as drafts and send posts to a review queue, so nothing goes live until it has been approved.',
    icon: icon(<><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></>) },
];

const WEEK = [
  { day: 'Mon', posts: [['facebook', 'Product launch'], ['linkedin', 'Hiring post']] },
  { day: 'Tue', posts: [['instagram', 'Behind the scenes']] },
  { day: 'Wed', posts: [['youtube', 'Tutorial video'], ['x', 'Event recap'], ['gmb', 'Weekend offer']] },
  { day: 'Thu', posts: [['linkedin', 'Case study']] },
  { day: 'Fri', posts: [['instagram', 'Team photo'], ['facebook', 'Weekly tips']] },
];

function ProductMock() {
  return (
    <div className="mock" aria-hidden="true">
      <div className="mock-bar"><i /><i /><i /><b>{SITE.name} · Calendar</b></div>
      <div className="mock-body">
        <div className="mock-head">This week <span>9 posts scheduled</span></div>
        <div className="mock-week">
          {WEEK.map(d => (
            <div key={d.day} className="mock-day">
              <small>{d.day}</small>
              {d.posts.map(([p, label]) => (
                <div key={label} className="mock-post" style={{ '--c': getPlatform(p).color }}><i />{label}</div>
              ))}
            </div>
          ))}
        </div>
        <div className="mock-composer">
          <div className="mock-line" style={{ width: '92%' }} />
          <div className="mock-line" style={{ width: '70%' }} />
          <div className="mock-foot">
            <div className="mock-chips">
              {SUPPORTED.map(p => <span key={p} className="mock-chip" style={{ background: getPlatform(p).color }}>{getPlatform(p).icon}</span>)}
            </div>
            <b>Schedule</b>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const user = useStore(s => s.user);
  useEffect(() => { document.title = `${SITE.name} — Social media scheduling for businesses`; }, []);

  return (
    <SiteLayout>
      <section className="site-hero">
        <div className="site-container site-hero-grid">
          <div>
            <div className="site-eyebrow">Social media scheduling</div>
            <h1>Plan once. <em>Publish everywhere.</em></h1>
            <p className="site-hero-lead">
              {SITE.name} lets businesses and agencies create, schedule and publish posts to Facebook, Instagram,
              LinkedIn, X, YouTube and Google Business Profile from a single dashboard.
            </p>
            <div className="site-hero-actions">
              {user ? (
                <Link to="/dashboard" className="site-btn site-btn-primary site-btn-lg">Open dashboard</Link>
              ) : <>
                <Link to="/register" className="site-btn site-btn-primary site-btn-lg">Create an account</Link>
                <Link to="/login" className="site-btn site-btn-outline site-btn-lg">Sign in</Link>
              </>}
            </div>
            <ul className="site-hero-points">
              <li>One calendar for every channel</li>
              <li>Per-platform captions</li>
              <li>Disconnect any account at any time</li>
            </ul>
          </div>
          <ProductMock />
        </div>
      </section>

      <section className="site-platforms" id="platforms">
        <div className="site-container">
          <p>Connect the platforms your business already uses</p>
          <div className="site-platform-row">
            {SUPPORTED.map(id => {
              const p = getPlatform(id);
              return <span key={id} className="site-platform"><i style={{ background: p.color }}>{p.icon}</i>{p.name === 'Google Business' ? 'Google Business Profile' : p.name}</span>;
            })}
          </div>
        </div>
      </section>

      <section className="site-section" id="features">
        <div className="site-container">
          <div className="site-section-head">
            <div className="site-eyebrow">Features</div>
            <h2>Everything you need to run your social calendar</h2>
            <p>Stop logging in to six different apps. Write, schedule and track your posts in one place.</p>
          </div>
          <div className="site-features">
            {FEATURES.map(f => (
              <div key={f.title} className="site-feature">
                <div className="site-feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="site-section alt" id="how-it-works">
        <div className="site-container">
          <div className="site-section-head">
            <div className="site-eyebrow">How it works</div>
            <h2>Up and running in minutes</h2>
            <p>Three steps from sign-up to your first scheduled post.</p>
          </div>
          <div className="site-steps">
            <div className="site-step">
              <h3>Create your account</h3>
              <p>Sign up with your email address. Each new account is reviewed and approved before it can be used.</p>
            </div>
            <div className="site-step">
              <h3>Connect your channels</h3>
              <p>Sign in to each platform through its official login screen and choose the pages, profiles or locations {SITE.name} may post to.</p>
            </div>
            <div className="site-step">
              <h3>Schedule and publish</h3>
              <p>Write your post, pick the accounts and a time. {SITE.name} publishes it for you and shows the result on your calendar.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="site-section" id="google">
        <div className="site-container site-split">
          <div>
            <div className="site-eyebrow">Your data, your control</div>
            <h2>Why {SITE.name} asks for access to your accounts</h2>
            <p>
              When you connect a channel, you grant {SITE.name} only the permissions it needs to publish the posts you create.
              We never post anything you have not written or scheduled yourself.
            </p>
            <ul className="site-checks">
              <li><span><strong>YouTube</strong>: uploads the videos you schedule to the channel you choose, and reads your channel name and picture so you can recognise it in {SITE.name}.</span></li>
              <li><span><strong>Google Business Profile</strong>: lists your business locations so you can pick one, and publishes the updates you schedule to it.</span></li>
              <li><span><strong>Sign-in details</strong>: your Google account name, email address and profile picture label the connected account in your dashboard.</span></li>
              <li><span>Access tokens are encrypted at rest. Disconnect an account or delete your {SITE.name} account at any time to remove them.</span></li>
            </ul>
          </div>
          <div className="site-panel">
            <h3>At a glance</h3>
            <div className="site-panel-row"><b>Do you sell my data?</b><span>No, never.</span></div>
            <div className="site-panel-row"><b>Is Google data used for ads?</b><span>No. Only to provide the features you use.</span></div>
            <div className="site-panel-row"><b>Is Google data used to train AI?</b><span>No.</span></div>
            <div className="site-panel-row"><b>Can I revoke access?</b><span>Yes, in {SITE.name} or in your Google Account settings.</span></div>
            <div className="site-panel-row"><b>Full details</b><span><Link to="/privacy">Read our Privacy Policy →</Link></span></div>
          </div>
        </div>
      </section>

      <section className="site-cta">
        <div className="site-container">
          <div className="site-cta-box">
            <h2>Start scheduling with {SITE.name}</h2>
            <p>Bring all your social channels together in one calendar.</p>
            {user
              ? <Link to="/dashboard" className="site-btn site-btn-white site-btn-lg">Open dashboard</Link>
              : <Link to="/register" className="site-btn site-btn-white site-btn-lg">Create your account</Link>}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
